import { supabase, run } from './db';
import { cached } from './local/cache';

/**
 * Estoque de sêmen: partidas, doses e saldo.
 *
 * Dose e gota não são a mesma conta, e é isto que o resto do arquivo assume:
 * a palheta (dose) é descongelada inteira e BAIXA o estoque; a gota é onde os
 * oócitos de uma doadora foram fecundados e serve para rastrear de qual
 * palheta ela saiu — não debita nada. Por isso a dose mora na sessão
 * (`fiv_semen_uso`) e a gota na linha da doadora (`fiv_donors.gotas`).
 *
 * A PARTIDA É UM SALDO, NÃO UM CONTADOR.
 * Entrada e saída são lançamentos datados em `semen_movimentos`, e o saldo é
 * a soma deles menos o que a FIV descongelou. Foi assim que as três coisas
 * que faltavam passaram a caber: o DONO (de quem é a palheta), a SOMA (duas
 * remessas do mesmo sêmen são uma partida com dois lançamentos) e as DATAS
 * (cada entrada e cada saída traz a sua, em vez de um contador que se
 * sobrescreve).
 *
 * NADA aqui é enfileirável, de propósito. Cadastrar partida é administração de
 * estoque, feita no laboratório e com sinal; e saldo é a única coisa do
 * sistema que dois aparelhos podem gastar ao mesmo tempo — enfileirar a baixa
 * deixaria os dois acharem que tinham palheta.
 *
 * O consumo em si NÃO passa por aqui: ele viaja dentro do payload da FIV, que
 * é enfileirável. Isso é intencional — o técnico lança a FIV inteira offline e
 * o estoque se acerta quando a sessão sobe.
 */

/**
 * Partidas do laboratório, já com saldo.
 *
 * Lê a view e não a tabela: `saldo_doses` é derivado de três parcelas
 * (iniciais − baixadas − usadas em FIV) e ninguém deveria recalcular isso do
 * lado de cá. Ordena por saldo zerado no fim — quem escolhe palheta quer ver
 * primeiro o que ainda existe.
 */
export const listSemenBatches = (laboratoryId) =>
  cached(`semen:${laboratoryId}`, () => run(supabase
    .from('vw_saldo_semen')
    .select('*')
    .eq('laboratory_id', laboratoryId)
    .order('saldo_doses', { ascending: false })
    .order('touro')));

/**
 * Partidas para o relatório, opcionalmente de um cliente só.
 *
 * Sem `cached` de propósito, como todo o resto dos relatórios: número velho
 * apresentado como atual leva a decidir errado — e este aqui é impresso e
 * ENVIADO, o que torna o erro permanente.
 *
 * Traz a partida ZERADA junto. Ela não suja o saldo (é zero) e responde uma
 * pergunta que o cliente faz: "e aquelas doses do CABARÉ que eu mandei?".
 * Sumir com a linha faria parecer que a partida nunca existiu.
 */
export const listarSemenDoRelatorio = ({ laboratoryId, clientId } = {}) => {
  if (!laboratoryId) return Promise.resolve([]);
  let q = supabase.from('vw_saldo_semen').select('*').eq('laboratory_id', laboratoryId);
  if (clientId) q = q.eq('client_id', clientId);
  return run(q.order('touro').order('codigo'));
};

/**
 * Cria a partida.
 *
 * O movimento de entrada NÃO é inserido aqui: um gatilho no banco
 * (`semen_batches_entrada_inicial`) o cria a partir de `doses_iniciais` e
 * `data_entrada`. Parece indireto e é de propósito — o mesmo gatilho é o que
 * segura o app antigo, ainda no ar entre a migration e o deploy, lançando
 * partida sem saber que o livro existe. Duas portas de entrada, um caminho só.
 */
export const insertSemenBatch = (row) =>
  run(supabase.from('semen_batches').insert([row]).select().single());

export const updateSemenBatch = (id, updates) =>
  run(supabase.from('semen_batches').update(updates).eq('id', id));

/**
 * Exclui a partida.
 *
 * Duas travas, e as duas pelo mesmo motivo. A FK de `fiv_semen_uso` é ON
 * DELETE RESTRICT: partida já usada em alguma FIV recusa a exclusão. E um
 * gatilho recusa partida que tenha QUALQUER saída lançada — o livro é apagado
 * em cascata com a partida, e sem essa trava excluir uma partida apagaria uma
 * venda em silêncio.
 *
 * Para partida acabada ou perdida existe a saída, que preserva a linha e a
 * história.
 */
export const deleteSemenBatch = (id) =>
  run(supabase.from('semen_batches').delete().eq('id', id));

/** Palhetas descongeladas numa sessão de FIV. */
export const fetchSemenDaSessao = (sessionId) =>
  run(supabase
    .from('fiv_semen_uso')
    .select('*')
    .eq('fiv_session_id', sessionId));

/**
 * Saldo que a partida teria se a sessão em edição fosse gravada agora.
 *
 * `saldo_doses` da view já desconta o que ESTA sessão gravou da última vez.
 * Sem devolver isso, abrir uma sessão que já consumiu 2 doses e não mexer em
 * nada mostraria o saldo descontado duas vezes — e a tela acusaria falta de
 * palheta que existe.
 *
 * Puro e exportado para ser testável: é a única conta desta tela que erra
 * calado.
 */
export const saldoProjetado = (saldoDaView, dosesJaGravadas, dosesAgora) =>
  Number(saldoDaView || 0) + Number(dosesJaGravadas || 0) - Number(dosesAgora || 0);

/**
 * Rótulo curto de uma partida, para caber em seletor de tabela.
 *
 * O dono entra quando existe. Na hora de escolher a palheta na bancada, saber
 * de quem ela é evita o erro que não tem volta: descongelar o sêmen de um
 * cliente na FIV de outro.
 */
export const rotuloDaPartida = (b) =>
  [b?.touro || 'sem touro', b?.codigo, b?.dono].filter(Boolean).join(' · ');

// Valor do filtro de dono para a partida sem cliente: '' já é "todos".
export const SEM_DONO = 'sem-dono';

const semAcento = (t) => String(t ?? '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * As partidas que o seletor de palhetas da FIV oferece.
 *
 * Só o que pode sair do botijão agora: com saldo, não descartada e ainda fora
 * da sessão (`fora`). A busca casa cada palavra em qualquer lugar — touro,
 * partida, dono, botijão, fornecedor —, sem acento e sem caixa: "cab sao" acha
 * o CABARÉ da Fazenda São Bento. Partidas do mesmo touro saem juntas.
 */
export const partidasParaEscolher = (batches = [], { busca = '', dono = '', fora = [] } = {}) => {
  const termos = semAcento(busca).split(/\s+/).filter(Boolean);
  const excluidas = new Set(fora);
  return batches
    .filter(b => !excluidas.has(b.semen_batch_id)
      && Number(b.saldo_doses) > 0
      && !b.descartado_em)
    .filter(b => !dono || (b.client_id || SEM_DONO) === dono)
    .filter(b => {
      const texto = semAcento([b.touro, b.codigo, b.dono, b.botijao, b.caneca, b.fornecedor].join(' '));
      return termos.every(t => texto.includes(t));
    })
    .sort((a, b) => (a.touro || '').localeCompare(b.touro || '', 'pt')
      || (a.codigo || '').localeCompare(b.codigo || '', 'pt'));
};

// ── O livro de movimentação ───────────────────────────────────────────────

/**
 * Motivos pelos quais uma palheta deixa o botijão fora da FIV.
 *
 * Lista fechada pelo mesmo motivo da saída de embrião: livre, "venda" e
 * "vendido" e "VENDA" viram três motivos no relatório. O banco repete a lista
 * num CHECK — se um id mudar aqui, muda lá também.
 *
 * `uso_externo` é o que não existia no embrião e existe no sêmen: palheta que
 * saiu para inseminação convencional, fora da FIV. Sem ele, todo esse consumo
 * viraria "descarte" e o relatório diria que o laboratório joga sêmen fora.
 */
export const MOTIVOS_DE_SAIDA_DE_SEMEN = [
  { id: 'venda',       label: 'Venda' },
  { id: 'devolucao',   label: 'Devolução ao dono' },
  { id: 'envio',       label: 'Envio a outro laboratório' },
  { id: 'uso_externo', label: 'Uso fora da FIV (IA)' },
  { id: 'descarte',    label: 'Descarte' },
  { id: 'perda',       label: 'Perda (palheta quebrada, botijão que subiu)' },
  { id: 'outro',       label: 'Outro' },
];

const MOTIVO_DE_SEMEN_POR_ID =
  Object.fromEntries(MOTIVOS_DE_SAIDA_DE_SEMEN.map(m => [m.id, m.label]));

export const rotuloDoMotivoDeSemen = (id) =>
  (id ? (MOTIVO_DE_SEMEN_POR_ID[id] || id) : '');

/** Como o banco normaliza código de partida no índice único. */
const codigoNormalizado = (c) => (c || '').trim().toUpperCase();

/**
 * A partida que já existe para este sêmen, se existir.
 *
 * ESTA FUNÇÃO É O PAR EXATO do índice `semen_batches_partida_unica`. A
 * identidade de uma partida é laboratório + touro + código + dono, e é por
 * isso que a segunda remessa do mesmo sêmen SOMA em vez de criar linha nova —
 * era o que não acontecia antes, e o usuário via "Já existe uma partida deste
 * touro com este código" com a entrada não entrando.
 *
 * O dono entra na chave porque sêmen de dois donos não se mistura, nem sendo
 * o mesmo touro e o mesmo lote: são duas pilhas de palheta com dois destinos
 * quando sobrar.
 *
 * Se esta regra e o índice divergirem, o app tenta inserir e o usuário leva
 * um 23505 na cara.
 */
export const achaPartida = (partidas = [], { bullId, codigo, clientId } = {}) =>
  partidas.find(p =>
    p.bull_id === bullId
    && codigoNormalizado(p.codigo) === codigoNormalizado(codigo)
    && (p.client_id || null) === (clientId || null)) || null;

/**
 * Dá entrada de doses.
 *
 * Duas portas para o mesmo lugar: sêmen que nunca entrou cria a partida (e o
 * gatilho do banco lança a entrada); sêmen que já está no botijão ganha mais
 * um lançamento na partida que existe.
 *
 * O ENDEREÇO SÓ É SOBRESCRITO QUANDO VEIO PREENCHIDO. Dar entrada não é hora
 * de apagar botijão e caneca: quem digita a segunda remessa às pressas deixa
 * os campos de local em branco, e zerar o endereço por causa disso é perder a
 * única informação que diz onde a palheta está.
 */
export const darEntradaDeSemen = async ({
  laboratoryId, partidas = [], bullId, clientId = null,
  codigo, fornecedor, doses, data, gotasPorDose,
  botijao, caneca, rack, altura, observacoes,
}) => {
  const qtd = Number(doses) || 0;
  if (!laboratoryId || !bullId) throw new Error('Escolha o touro no cadastro.');
  if (qtd <= 0) throw new Error('Informe quantas doses entraram.');

  const texto = (v) => (typeof v === 'string' ? v.trim() : v) || null;
  const local = {
    botijao: texto(botijao), caneca: texto(caneca),
    rack: texto(rack), altura: altura || null,
  };

  const existente = achaPartida(partidas, { bullId, codigo, clientId });

  if (!existente) {
    const partida = await insertSemenBatch({
      laboratory_id:  laboratoryId,
      bull_id:        bullId,
      client_id:      clientId || null,
      codigo:         texto(codigo),
      fornecedor:     texto(fornecedor),
      doses_iniciais: qtd,
      // Sem data informada o banco usa `current_date`; mandar `null` violaria
      // o NOT NULL da coluna.
      ...(data ? { data_entrada: data } : {}),
      gotas_por_dose: parseInt(gotasPorDose) || null,
      observacoes:    texto(observacoes),
      ...local,
    });
    return { partida, somou: false, doses: qtd, saldo: qtd };
  }

  await run(supabase.from('semen_movimentos').insert([{
    laboratory_id:  laboratoryId,
    semen_batch_id: existente.semen_batch_id,
    tipo:           'entrada',
    doses:          qtd,
    ...(data ? { data } : {}),
    destino:        texto(fornecedor),
    observacoes:    texto(observacoes),
  }]));

  const remendo = Object.fromEntries(
    Object.entries(local).filter(([, v]) => v !== null));
  if (texto(fornecedor))      remendo.fornecedor     = texto(fornecedor);
  if (parseInt(gotasPorDose)) remendo.gotas_por_dose = parseInt(gotasPorDose);
  if (Object.keys(remendo).length) {
    await updateSemenBatch(existente.semen_batch_id, remendo);
  }

  return {
    partida: existente,
    somou:   true,
    doses:   qtd,
    saldo:   Number(existente.saldo_doses || 0) + qtd,
  };
};

/**
 * Tira doses do estoque fora da bancada.
 *
 * O que é descongelado numa FIV NÃO passa por aqui — sai sozinho pela sessão,
 * e lançar as duas coisas faria a mesma palheta baixar duas vezes.
 *
 * A saída não trava no saldo. Quem está com o botijão aberto sabe o que tirou
 * melhor que o sistema, e recusar o lançamento faria a pessoa registrar
 * errado para conseguir salvar. Saldo negativo é sinal de estoque
 * desatualizado, e a tela mostra isso em vez de esconder.
 */
export const darSaidaDeSemen = (partida, {
  doses, data, motivo, destino, observacoes,
} = {}) => {
  const qtd = Number(doses) || 0;
  if (!partida?.semen_batch_id) throw new Error('Partida não informada.');
  if (qtd <= 0) throw new Error('Informe quantas doses saíram.');
  if (!motivo)  throw new Error('Informe o motivo da saída.');
  if (!data)    throw new Error('Informe a data da saída.');

  return run(supabase.from('semen_movimentos').insert([{
    laboratory_id:  partida.laboratory_id,
    semen_batch_id: partida.semen_batch_id,
    tipo:           'saida',
    doses:          qtd,
    data,
    motivo,
    destino:        destino?.trim() || null,
    observacoes:    observacoes?.trim() || null,
  }]));
};

/**
 * O extrato de uma partida: entradas, saídas e o que a FIV descongelou.
 *
 * Vem da view que une as duas tabelas, porque a pergunta de quem abre isto é
 * "o que aconteceu com esta palheta" — e metade do que sai do botijão sai
 * pela bancada. Mais recente primeiro: extrato se lê de cima.
 */
export const listarMovimentosDaPartida = (semenBatchId) =>
  run(supabase
    .from('vw_movimentos_semen')
    .select('*')
    .eq('semen_batch_id', semenBatchId)
    .order('data', { ascending: false })
    .order('created_at', { ascending: false }));

/** Apaga um lançamento digitado errado. Só o que foi lançado pela tela. */
export const excluirMovimento = (id) =>
  run(supabase.from('semen_movimentos').delete().eq('id', id));

/**
 * Lançamento que a tela deixa apagar.
 *
 * Consumo de FIV não: ele pertence à sessão e some de lá, junto com a palheta
 * que a sessão diz ter descongelado. E a entrada inicial também não: ela é a
 * partida em si, e apagá-la deixaria uma partida sem começo, com saldo
 * negativo. Para desfazer a entrada inicial, exclui-se a partida.
 */
export const movimentoApagavel = (m) =>
  m?.tipo !== 'fiv' && m?.origem === 'lancamento';

// ── Contas puras, testáveis ───────────────────────────────────────────────

/**
 * Agrupa as partidas por touro.
 *
 * É a resposta para "quantas doses de CABARÉ eu tenho", que é como a pergunta
 * é feita na prática — ninguém pergunta por lote. Duas partidas do mesmo touro
 * (códigos diferentes, ou donos diferentes) aparecem juntas e somadas, sem
 * deixar de ser duas linhas: o lote e o dono continuam sendo o que se confere
 * contra a etiqueta.
 *
 * Touro sem saldo nenhum vai para o fim: quem abre esta tela quer ver o que
 * ainda existe.
 */
export const agruparPorTouro = (partidas = []) => {
  const mapa = new Map();
  for (const p of partidas) {
    const k = p.bull_id || p.touro || 'sem-touro';
    if (!mapa.has(k)) {
      mapa.set(k, { bull_id: p.bull_id || null, touro: p.touro || '—', saldo: 0, partidas: [] });
    }
    const g = mapa.get(k);
    g.saldo += Number(p.saldo_doses) || 0;
    g.partidas.push(p);
  }
  return [...mapa.values()].sort((a, b) => {
    const aVazio = a.saldo <= 0, bVazio = b.saldo <= 0;
    if (aVazio !== bVazio) return aVazio ? 1 : -1;
    return a.touro.localeCompare(b.touro);
  });
};

/**
 * Os números do topo da tela.
 *
 * `deClientes` existe separado do total porque é a pergunta que o dono do
 * laboratório faz olhando o botijão: quanto disto é meu, de verdade.
 */
export const resumoDoSemen = (partidas = []) => {
  const comSaldo = partidas.filter(p => Number(p.saldo_doses) > 0);
  const soma = (ls) => ls.reduce((s, p) => s + (Number(p.saldo_doses) || 0), 0);
  return {
    doses:      soma(comSaldo),
    doLab:      soma(comSaldo.filter(p => !p.client_id)),
    deClientes: soma(comSaldo.filter(p => p.client_id)),
    touros:     new Set(comSaldo.map(p => p.bull_id || p.touro)).size,
    partidas:   partidas.length,
    zeradas:    partidas.length - comSaldo.length,
  };
};

/**
 * O livro da partida fechado: entradas − saídas − usadas = saldo.
 *
 * As quatro parcelas juntas porque é assim que a conta se CONFERE. Mostrar só
 * o saldo obriga quem recebe a folha a acreditar; mostrar as quatro deixa
 * somar e bater — e é para isso que a linha do meio existe num extrato.
 *
 * `usadas` é o que a FIV descongelou, e ele NÃO passa por `semen_movimentos`:
 * vive em `fiv_semen_uso`, e a view já o traz somado. Sem essa parcela a conta
 * não fecha, e a diferença parece palheta sumida.
 */
export const totaisDoLivro = (partidas = []) => {
  const soma = (campo) => (partidas || [])
    .reduce((t, p) => t + (Number(p?.[campo]) || 0), 0);
  // Arredonda na casa da dose: somar meias doses em ponto flutuante rende
  // 26.999999999, e relatório impresso não pode mostrar isso.
  const cem = (n) => Math.round(n * 100) / 100;
  return {
    entradas: cem(soma('doses_entradas')),
    saidas:   cem(soma('doses_saidas')),
    usadas:   cem(soma('doses_usadas')),
    saldo:    cem(soma('saldo_doses')),
  };
};

/**
 * Doses como quem conta palheta escreve: "20", "2,5", "0,25".
 *
 * Três regras, e cada uma corrige uma leitura errada:
 *
 *   - palheta inteira não ganha casa decimal à toa ("20", não "20,00"): o
 *     saldo é palheta contada, e a casa decimal sugeria uma precisão que a
 *     bancada não tem;
 *   - a fração aparece com VÍRGULA, porque "2.5" no meio de uma tela em
 *     português se lê como dois mil e quinhentos;
 *   - o zero à direita cai ("2,5", não "2,50"). Meia dose é o caso comum
 *     desde que a palheta passou a ser dividida, e ela se escreve com uma
 *     casa. O quarto de dose ainda aparece inteiro, porque só o zero SOBRANDO
 *     é removido.
 *
 * O `toFixed(2)` fica: ele é o que arredonda a sujeira de ponto flutuante
 * antes de a tela mostrar "2,4999999999" de uma soma de meias doses.
 */
export const emDoses = (v) => {
  const n = Number(v) || 0;
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2).replace(/0$/, '').replace('.', ',');
};

/** Data do banco (ISO) no formato de quem lê a tela. Vazia não vira 1969. */
export const dataCurta = (iso) => {
  if (!iso) return '';
  const [a, m, d] = String(iso).slice(0, 10).split('-');
  return d ? `${d}/${m}/${a}` : '';
};
